import {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent as ReactDragEvent,
} from 'react';

import type {
  ComponentKind,
  OptionDef,
  ProductType,
  Question,
  Step,
  SurveyBuilderAppContext,
  SurveyTree,
  SurveyValidationResult,
} from './types';
import { validateTree } from './validate';

const DEFAULT_PRODUCT: Record<SurveyBuilderAppContext['app'], ProductType> = {
  xotski: 'consumer-survey',
  console: 'lead-form',
  xamplin: 'brand-study',
  survey: 'consumer-survey',
  onxite: 'feedback',
};

const QUESTION_KINDS: ComponentKind[] = [
  'text',
  'email',
  'phone',
  'singleChoice',
  'multiChoice',
  'rating',
  'image',
  'wheel-link',
];

const SPECIAL_FIELDS: Question['specialField'][] = [
  undefined,
  'fullName',
  'email',
  'phone',
  'age',
  'gender',
  'ethnicity',
  'zipCode',
] as const as Question['specialField'][];

// Caso 08 v2 fix — native <select> styling. A transparent select with no
// explicit colors renders its option popup with browser defaults, which
// on a dark host means near-invisible dark-on-dark text. Give the select
// a solid elevated background + foreground colour, and set the SAME
// explicit colours on every <option> (the popup list ignores inherited
// CSS in most browsers — inline style on the option is what sticks).
const SELECT_CLASS =
  'rounded border border-[var(--color-border-default,#444)] ' +
  'bg-[var(--color-bg-elevated,#1c1c1c)] text-[var(--color-fg-primary,#f5f5f3)] ' +
  'px-2 py-1 text-xs';

const OPTION_STYLE: CSSProperties = {
  backgroundColor: 'var(--color-bg-elevated, #1c1c1c)',
  color: 'var(--color-fg-primary, #f5f5f3)',
};

export interface SurveyBuilderProps {
  /** Initial tree. Pass an empty stub for new surveys. */
  tree: SurveyTree;
  appContext: SurveyBuilderAppContext;
  /** `edit` shows the editor; `view` renders read-only summary. */
  mode?: 'edit' | 'view';
  /**
   * Persistence callback. The package never knows about your API — the
   * host is responsible for transactional writes and conflict resolution.
   */
  onSave?: (next: SurveyTree) => Promise<void> | void;
  /**
   * Server-side validation hook. The package's local validator runs
   * first; `onValidate` is for cross-survey checks the backend owns.
   */
  onValidate?: (next: SurveyTree) => Promise<SurveyValidationResult>;
  /**
   * Caso 08 v4 — Debounce window for autosave, in ms. `0` (default)
   * disables autosave; the user must press Save explicitly. Recommended
   * 1500–3000 ms once the host's PUT endpoint is fast.
   *
   * Autosave is skipped when the local validator reports errors so we
   * never persist a broken tree silently.
   */
  autoSaveMs?: number;
}

/**
 * Caso 08 v1 — Embeddable survey builder with drag-and-drop step + question
 * reordering, inline editing, option editor, and live validation.
 *
 * All state is local; the host controls persistence via `onSave`. The tree
 * shape (`SurveyTree`) is the stable contract — adding fields is safe,
 * renaming is a breaking change.
 *
 * The drag-and-drop uses the platform HTML5 API (no dependency); pressing
 * the up/down arrows on the focused row is an a11y-friendly alternative.
 */
export function SurveyBuilder({
  tree,
  appContext,
  mode = 'edit',
  onSave,
  onValidate,
  autoSaveMs = 0,
}: SurveyBuilderProps) {
  const [local, setLocal] = useState<SurveyTree>(tree);
  const [issues, setIssues] = useState<SurveyValidationResult | null>(null);
  const [saving, setSaving] = useState(false);
  // Caso 08 v2 — Active language for the editor. When equal to
  // `defaultLanguage` the inputs read/write the base fields directly;
  // for any other language they read/write `translations[lang]` and the
  // base value renders as the placeholder so the editor has context.
  const [activeLang, setActiveLang] = useState<string>(
    tree.defaultLanguage ?? (tree.languages?.[0] ?? 'en'),
  );
  const defaultLang = local.defaultLanguage ?? 'en';
  const isDefaultLang = activeLang === defaultLang;
  // Caso 08 v4 — Autosave bookkeeping. `autoStatus` drives the small
  // indicator in the footer ("Autosaved", "Autosaving…", "Autosave off");
  // `lastSavedSig` snapshots the tree we wrote last so consecutive
  // identical autoSave triggers (the same render firing twice) are no-ops.
  const [autoStatus, setAutoStatus] = useState<
    'idle' | 'saving' | 'saved' | 'error'
  >('idle');
  const lastSavedSig = useRef<string>(JSON.stringify(tree));
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const productType =
    local.productType ?? appContext.productType ?? DEFAULT_PRODUCT[appContext.app];

  // Autosave effect — runs whenever `local` changes and autosave is on.
  // The validator gate prevents accidental persistence of invalid drafts;
  // the user still sees the explicit Save button for those cases.
  useEffect(() => {
    if (autoSaveMs <= 0 || mode !== 'edit' || !onSave) return;
    const sig = JSON.stringify(local);
    if (sig === lastSavedSig.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const localResult = validateTree(local);
      if (!localResult.ok) return; // wait for explicit Save on errors
      setAutoStatus('saving');
      try {
        await onSave(local);
        lastSavedSig.current = sig;
        setAutoStatus('saved');
      } catch {
        setAutoStatus('error');
      }
    }, autoSaveMs);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [local, autoSaveMs, mode, onSave]);

  // ── State helpers ────────────────────────────────────────────────────
  const updateStep = useCallback(
    (stepId: string, patch: Partial<Step>) =>
      setLocal((t) => ({
        ...t,
        steps: t.steps.map((s) => (s.id === stepId ? { ...s, ...patch } : s)),
      })),
    [],
  );

  const updateQuestion = useCallback(
    (stepId: string, questionId: string, patch: Partial<Question>) =>
      setLocal((t) => ({
        ...t,
        steps: t.steps.map((s) =>
          s.id === stepId
            ? {
                ...s,
                questions: s.questions.map((q) =>
                  q.id === questionId ? { ...q, ...patch } : q,
                ),
              }
            : s,
        ),
      })),
    [],
  );

  const addStep = useCallback(() => {
    setLocal((t) => ({
      ...t,
      steps: [
        ...t.steps,
        {
          id: genId('step'),
          order: t.steps.length + 1,
          title: '',
          questions: [],
        },
      ],
    }));
  }, []);

  const removeStep = useCallback((stepId: string) => {
    setLocal((t) => ({
      ...t,
      steps: t.steps
        .filter((s) => s.id !== stepId)
        .map((s, i) => ({ ...s, order: i + 1 })),
    }));
  }, []);

  const reorderSteps = useCallback((from: number, to: number) => {
    if (from === to) return;
    setLocal((t) => {
      const next = [...t.steps];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return { ...t, steps: next.map((s, i) => ({ ...s, order: i + 1 })) };
    });
  }, []);

  const addQuestion = useCallback((stepId: string) => {
    setLocal((t) => ({
      ...t,
      steps: t.steps.map((s) =>
        s.id === stepId
          ? {
              ...s,
              questions: [
                ...s.questions,
                {
                  id: genId('q'),
                  kind: 'text',
                  label: '',
                  required: false,
                },
              ],
            }
          : s,
      ),
    }));
  }, []);

  const removeQuestion = useCallback((stepId: string, questionId: string) => {
    setLocal((t) => ({
      ...t,
      steps: t.steps.map((s) =>
        s.id === stepId
          ? { ...s, questions: s.questions.filter((q) => q.id !== questionId) }
          : s,
      ),
    }));
  }, []);

  const reorderQuestions = useCallback(
    (stepId: string, from: number, to: number) => {
      if (from === to) return;
      setLocal((t) => ({
        ...t,
        steps: t.steps.map((s) => {
          if (s.id !== stepId) return s;
          const next = [...s.questions];
          const [moved] = next.splice(from, 1);
          next.splice(to, 0, moved);
          return { ...s, questions: next };
        }),
      }));
    },
    [],
  );

  // ── Save / validate handlers ────────────────────────────────────────
  async function handleValidate() {
    const local0 = validateTree(local);
    if (!local0.ok || !onValidate) {
      setIssues(local0);
      return;
    }
    const remote = await onValidate(local);
    setIssues({
      ok: local0.ok && remote.ok,
      issues: [...local0.issues, ...remote.issues],
    });
  }

  async function handleSave() {
    const local0 = validateTree(local);
    setIssues(local0);
    if (!local0.ok || !onSave) return;
    setSaving(true);
    try {
      await onSave(local);
      // Keep the autosave bookkeeping in sync so a manual save doesn't
      // immediately re-fire the debounced autosave.
      lastSavedSig.current = JSON.stringify(local);
      setAutoStatus('saved');
    } finally {
      setSaving(false);
    }
  }

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div
      data-ox-survey-builder=""
      data-mode={mode}
      data-app={appContext.app}
      className="ox-survey-builder flex flex-col gap-4"
    >
      <header className="ox-survey-builder__header flex items-start justify-between gap-3">
        <div className="flex-1">
          <input
            aria-label="Survey name"
            value={local.name}
            onChange={(e) => setLocal({ ...local, name: e.target.value })}
            disabled={mode === 'view'}
            placeholder="Untitled survey"
            className="w-full bg-transparent text-xl font-semibold outline-none"
          />
          <div className="mt-1 text-xs text-[var(--color-fg-muted,#888)]">
            {productType} · {local.steps.length} step
            {local.steps.length === 1 ? '' : 's'} · {local.languages?.length ?? 1}{' '}
            language{(local.languages?.length ?? 1) === 1 ? '' : 's'}
          </div>
        </div>
      </header>

      {/* Caso 08 v2 — Language tabs. Hidden when the survey is
          single-language (1 entry in `languages` or none). Adding a
          language is one click; removing is gated behind a confirm so
          the user can't nuke a tab of translations by accident. */}
      <LanguageTabs
        languages={local.languages ?? [defaultLang]}
        defaultLang={defaultLang}
        active={activeLang}
        readOnly={mode === 'view'}
        onSelect={setActiveLang}
        onAdd={(code) => {
          const next = new Set(local.languages ?? [defaultLang]);
          next.add(code);
          setLocal({ ...local, languages: Array.from(next) });
          setActiveLang(code);
        }}
        onRemove={(code) => {
          if (code === defaultLang) return; // never drop the default
          const next = (local.languages ?? []).filter((l) => l !== code);
          setLocal({ ...local, languages: next });
          if (activeLang === code) setActiveLang(defaultLang);
        }}
      />

      {/* Steps list */}
      <ol className="flex flex-col gap-3">
        {local.steps.map((step, sIdx) => (
          <StepCard
            key={step.id}
            step={step}
            stepIndex={sIdx}
            stepsCount={local.steps.length}
            readOnly={mode === 'view'}
            activeLang={activeLang}
            isDefaultLang={isDefaultLang}
            onUpdate={(patch) => updateStep(step.id, patch)}
            onRemove={() => removeStep(step.id)}
            onMoveStep={(to) => reorderSteps(sIdx, to)}
            onUpdateQuestion={(qId, patch) => updateQuestion(step.id, qId, patch)}
            onRemoveQuestion={(qId) => removeQuestion(step.id, qId)}
            onAddQuestion={() => addQuestion(step.id)}
            onReorderQuestions={(from, to) =>
              reorderQuestions(step.id, from, to)
            }
          />
        ))}
      </ol>

      {mode === 'edit' && (
        <button
          type="button"
          onClick={addStep}
          className="rounded-lg border border-dashed border-[var(--color-border-default,#444)] py-3 text-sm font-semibold text-[var(--color-fg-muted,#888)] hover:border-[var(--color-accent,#b262f5)] hover:text-[var(--color-accent,#b262f5)]"
        >
          + Add step
        </button>
      )}

      {/* Issues panel */}
      {issues && (
        <ul
          role="status"
          className="space-y-1 rounded-md border border-[var(--color-border-subtle,#333)] p-3 text-xs"
        >
          {issues.issues.map((issue, i) => (
            <li
              key={`${issue.path}-${i}`}
              data-severity={issue.severity}
              className={
                issue.severity === 'error'
                  ? 'text-[var(--color-error,#ff5a4f)]'
                  : 'text-[var(--color-warn,#f4b740)]'
              }
            >
              <code className="font-mono">{issue.path}</code>: {issue.message}
            </li>
          ))}
          {issues.ok && issues.issues.length === 0 && (
            <li className="text-[var(--color-success,#4dd28b)]">
              No issues. Ready to save.
            </li>
          )}
        </ul>
      )}

      {/* Actions */}
      {mode === 'edit' && (
        <footer className="flex items-center justify-end gap-2">
          {autoSaveMs > 0 && (
            <span
              className="mr-auto text-xs text-[var(--color-fg-muted,#888)]"
              data-autosave-status={autoStatus}
            >
              {autoStatus === 'saving'
                ? 'Autosaving…'
                : autoStatus === 'saved'
                ? 'Autosaved'
                : autoStatus === 'error'
                ? 'Autosave failed — try the Save button'
                : 'Autosave on'}
            </span>
          )}
          <button
            type="button"
            onClick={handleValidate}
            className="rounded-md border border-[var(--color-border-default,#444)] px-3 py-1.5 text-sm hover:border-[var(--color-accent,#b262f5)]"
          >
            Validate
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-md bg-[var(--color-accent,#b262f5)] px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </footer>
      )}
    </div>
  );
}

// ── StepCard ──────────────────────────────────────────────────────────
function StepCard({
  step,
  stepIndex,
  stepsCount,
  readOnly,
  activeLang,
  isDefaultLang,
  onUpdate,
  onRemove,
  onMoveStep,
  onUpdateQuestion,
  onRemoveQuestion,
  onAddQuestion,
  onReorderQuestions,
}: {
  step: Step;
  stepIndex: number;
  stepsCount: number;
  readOnly: boolean;
  activeLang: string;
  isDefaultLang: boolean;
  onUpdate: (patch: Partial<Step>) => void;
  onRemove: () => void;
  onMoveStep: (to: number) => void;
  onUpdateQuestion: (qId: string, patch: Partial<Question>) => void;
  onRemoveQuestion: (qId: string) => void;
  onAddQuestion: () => void;
  onReorderQuestions: (from: number, to: number) => void;
}) {
  // Step drag-and-drop reorders steps at the parent; question drag-and-drop
  // reorders within this step.
  const stepDrag = useDragHandlers<number>((_from, to) => onMoveStep(to));
  const qDrag = useDragHandlers<number>((from, to) => onReorderQuestions(from, to));

  return (
    <li
      className="rounded-lg border border-[var(--color-border-default,#444)] bg-[var(--color-bg-surface,#141414)] p-3"
      draggable={!readOnly}
      onDragStart={(e) => stepDrag.onDragStart(e, stepIndex)}
      onDragOver={(e) => stepDrag.onDragOver(e, stepIndex)}
      onDrop={(e) => stepDrag.onDrop(e, stepIndex)}
      onDragEnd={stepDrag.onDragEnd}
    >
      <div className="mb-2 flex items-center gap-2">
        <span
          className="cursor-grab select-none text-xs text-[var(--color-fg-subtle,#666)]"
          aria-label="Drag step"
        >
          ⠿
        </span>
        <span className="text-xs font-semibold text-[var(--color-fg-muted,#888)]">
          Step {stepIndex + 1}
        </span>
        <input
          aria-label="Step title"
          value={step.title ?? ''}
          onChange={(e) => onUpdate({ title: e.target.value })}
          disabled={readOnly}
          placeholder="Untitled step"
          className="flex-1 bg-transparent text-sm font-medium outline-none"
        />
        {!readOnly && (
          <Fragment>
            <button
              type="button"
              onClick={() => onMoveStep(Math.max(0, stepIndex - 1))}
              disabled={stepIndex === 0}
              className="rounded p-1 text-xs disabled:opacity-30 hover:bg-[var(--color-bg-elevated,#1c1c1c)]"
              aria-label="Move step up"
            >
              ↑
            </button>
            <button
              type="button"
              onClick={() => onMoveStep(Math.min(stepsCount - 1, stepIndex + 1))}
              disabled={stepIndex === stepsCount - 1}
              className="rounded p-1 text-xs disabled:opacity-30 hover:bg-[var(--color-bg-elevated,#1c1c1c)]"
              aria-label="Move step down"
            >
              ↓
            </button>
            <button
              type="button"
              onClick={onRemove}
              className="rounded p-1 text-xs text-[var(--color-error,#ff5a4f)] hover:bg-[var(--color-bg-elevated,#1c1c1c)]"
              aria-label="Remove step"
            >
              ✕
            </button>
          </Fragment>
        )}
      </div>

      <ul className="flex flex-col gap-2 pl-4">
        {step.questions.map((q, qIdx) => (
          <QuestionRow
            key={q.id}
            question={q}
            questionIndex={qIdx}
            readOnly={readOnly}
            activeLang={activeLang}
            isDefaultLang={isDefaultLang}
            onUpdate={(patch) => onUpdateQuestion(q.id, patch)}
            onRemove={() => onRemoveQuestion(q.id)}
            dragHandlers={qDrag}
          />
        ))}
      </ul>

      {!readOnly && (
        <button
          type="button"
          onClick={onAddQuestion}
          className="ml-4 mt-2 text-xs text-[var(--color-accent,#b262f5)] hover:underline"
        >
          + Add question
        </button>
      )}
    </li>
  );
}

// ── QuestionRow ───────────────────────────────────────────────────────
function QuestionRow({
  question,
  questionIndex,
  readOnly,
  activeLang,
  isDefaultLang,
  onUpdate,
  onRemove,
  dragHandlers,
}: {
  question: Question;
  questionIndex: number;
  readOnly: boolean;
  activeLang: string;
  isDefaultLang: boolean;
  onUpdate: (patch: Partial<Question>) => void;
  onRemove: () => void;
  dragHandlers: ReturnType<typeof useDragHandlers<number>>;
}) {
  const isChoice =
    question.kind === 'singleChoice' || question.kind === 'multiChoice';

  // Caso 08 v2 — Compute the displayed label for the current language.
  // Default language reads/writes `question.label`; any other reads
  // `translations[lang].label`, falling back to '' so the placeholder
  // shows the base label as a hint.
  const displayedLabel = isDefaultLang
    ? question.label
    : question.translations?.[activeLang]?.label ?? '';

  function writeLabel(next: string) {
    if (isDefaultLang) {
      onUpdate({ label: next });
      return;
    }
    const t = { ...(question.translations ?? {}) };
    t[activeLang] = { ...(t[activeLang] ?? {}), label: next };
    onUpdate({ translations: t });
  }

  return (
    <li
      className="rounded-md border border-[var(--color-border-subtle,#333)] bg-[var(--color-bg-elevated,#1c1c1c)] p-2"
      draggable={!readOnly}
      onDragStart={(e) => dragHandlers.onDragStart(e, questionIndex)}
      onDragOver={(e) => dragHandlers.onDragOver(e, questionIndex)}
      onDrop={(e) => dragHandlers.onDrop(e, questionIndex)}
      onDragEnd={dragHandlers.onDragEnd}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span
          className="cursor-grab select-none text-xs text-[var(--color-fg-subtle,#666)]"
          aria-label="Drag question"
        >
          ⠿
        </span>
        <input
          aria-label="Question label"
          value={displayedLabel}
          onChange={(e) => writeLabel(e.target.value)}
          disabled={readOnly}
          placeholder={
            isDefaultLang
              ? 'Question text'
              : question.label || 'Question text'
          }
          className="min-w-[180px] flex-1 bg-transparent text-sm outline-none"
        />
        <select
          aria-label="Question kind"
          value={question.kind}
          onChange={(e) =>
            onUpdate({ kind: e.target.value as ComponentKind })
          }
          disabled={readOnly}
          className={SELECT_CLASS}
        >
          {QUESTION_KINDS.map((k) => (
            <option key={k} value={k} style={OPTION_STYLE}>
              {k}
            </option>
          ))}
        </select>
        <select
          aria-label="Special field"
          value={question.specialField ?? ''}
          onChange={(e) =>
            onUpdate({
              specialField:
                (e.target.value as Question['specialField']) || undefined,
            })
          }
          disabled={readOnly}
          className={SELECT_CLASS}
        >
          {SPECIAL_FIELDS.map((sf) => (
            <option key={sf ?? ''} value={sf ?? ''} style={OPTION_STYLE}>
              {sf ?? '(none)'}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1 text-xs">
          <input
            type="checkbox"
            checked={!!question.required}
            onChange={(e) => onUpdate({ required: e.target.checked })}
            disabled={readOnly}
          />
          required
        </label>
        {!readOnly && (
          <button
            type="button"
            onClick={onRemove}
            className="ml-auto rounded p-1 text-xs text-[var(--color-error,#ff5a4f)]"
            aria-label="Remove question"
          >
            ✕
          </button>
        )}
      </div>

      {isChoice && (
        <OptionsEditor
          options={question.options ?? []}
          readOnly={readOnly}
          activeLang={activeLang}
          isDefaultLang={isDefaultLang}
          onChange={(opts) => onUpdate({ options: opts })}
        />
      )}
    </li>
  );
}

// ── OptionsEditor ─────────────────────────────────────────────────────
function OptionsEditor({
  options,
  readOnly,
  activeLang,
  isDefaultLang,
  onChange,
}: {
  options: OptionDef[];
  readOnly: boolean;
  activeLang: string;
  isDefaultLang: boolean;
  onChange: (next: OptionDef[]) => void;
}) {
  function update(idx: number, patch: Partial<OptionDef>) {
    onChange(options.map((o, i) => (i === idx ? { ...o, ...patch } : o)));
  }
  function remove(idx: number) {
    onChange(options.filter((_, i) => i !== idx));
  }
  function add() {
    onChange([
      ...options,
      { optionKey: genId('opt'), label: '' },
    ]);
  }

  // Caso 08 v2 — per-option translation helpers mirroring the question
  // label helpers in QuestionRow.
  function displayedLabel(opt: OptionDef): string {
    if (isDefaultLang) return opt.label;
    return opt.translations?.[activeLang]?.label ?? '';
  }
  function writeLabel(idx: number, next: string) {
    const opt = options[idx];
    if (isDefaultLang) {
      const patch: Partial<OptionDef> = { label: next };
      // Auto-derive optionKey only while editing the default language and
      // the key is still the pristine generated id.
      if (opt.optionKey.startsWith('opt-')) {
        const slug = slugify(next);
        if (slug) patch.optionKey = slug;
      }
      update(idx, patch);
      return;
    }
    const t = { ...(opt.translations ?? {}) };
    const prev = (t[activeLang] ?? {}) as { label?: string };
    t[activeLang] = { ...prev, label: next };
    update(idx, { translations: t });
  }

  return (
    <div className="mt-2 ml-4 flex flex-col gap-1">
      {options.map((opt, idx) => (
        <div key={opt.optionKey} className="flex items-center gap-2">
          <input
            aria-label="Option label"
            value={displayedLabel(opt)}
            onChange={(e) => writeLabel(idx, e.target.value)}
            disabled={readOnly}
            placeholder={
              isDefaultLang
                ? 'Option label'
                : opt.label || 'Option label'
            }
            className="flex-1 rounded bg-transparent px-2 py-1 text-xs outline-none"
          />
          <input
            aria-label="Option key"
            value={opt.optionKey}
            onChange={(e) => update(idx, { optionKey: e.target.value })}
            // optionKey is shared across languages — only the default-language
            // editor can rename it.
            disabled={readOnly || !isDefaultLang}
            placeholder="optionKey"
            className="w-32 rounded bg-transparent px-2 py-1 font-mono text-[10px] outline-none disabled:opacity-50"
          />
          {!readOnly && isDefaultLang && (
            <button
              type="button"
              onClick={() => remove(idx)}
              className="rounded p-1 text-xs text-[var(--color-error,#ff5a4f)]"
              aria-label="Remove option"
            >
              ✕
            </button>
          )}
        </div>
      ))}
      {!readOnly && isDefaultLang && (
        <button
          type="button"
          onClick={add}
          className="self-start text-xs text-[var(--color-accent,#b262f5)] hover:underline"
        >
          + Add option
        </button>
      )}
    </div>
  );
}

// ── LanguageTabs (Caso 08 v2) ─────────────────────────────────────────
function LanguageTabs({
  languages,
  defaultLang,
  active,
  readOnly,
  onSelect,
  onAdd,
  onRemove,
}: {
  languages: string[];
  defaultLang: string;
  active: string;
  readOnly: boolean;
  onSelect: (code: string) => void;
  onAdd: (code: string) => void;
  onRemove: (code: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [draftCode, setDraftCode] = useState('');

  // Hide entirely when there's only the default language and the host
  // didn't opt-in via the languages array. v2 v.next: an "Add language"
  // affordance can still appear here if the host wants to support i18n
  // on a single-language survey — gated behind onAdd existing.
  if (languages.length === 0) return null;

  function commitAdd() {
    const code = draftCode.trim().toLowerCase();
    if (!code || code.length > 8 || languages.includes(code)) {
      setAdding(false);
      setDraftCode('');
      return;
    }
    onAdd(code);
    setAdding(false);
    setDraftCode('');
  }

  return (
    <div
      role="tablist"
      aria-label="Survey languages"
      className="flex flex-wrap items-center gap-1 border-b border-[var(--color-border-subtle,#333)] pb-1 text-xs"
    >
      {languages.map((code) => {
        const isActive = code === active;
        const isDefault = code === defaultLang;
        return (
          <span
            key={code}
            className={
              'inline-flex items-center gap-1 rounded-md px-2 py-1 ' +
              (isActive
                ? 'bg-[var(--color-accent-soft,rgba(178,98,245,0.12))] font-semibold text-[var(--color-fg-primary,#fff)]'
                : 'text-[var(--color-fg-muted,#888)] hover:bg-[var(--color-bg-elevated,#1c1c1c)]')
            }
          >
            <button
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onSelect(code)}
              className="uppercase"
            >
              {code}
              {isDefault && (
                <span className="ml-1 text-[9px] opacity-60">(base)</span>
              )}
            </button>
            {!readOnly && !isDefault && (
              <button
                type="button"
                onClick={() => onRemove(code)}
                aria-label={`Remove language ${code}`}
                className="rounded-full px-1 text-[10px] text-[var(--color-fg-muted,#888)] hover:bg-[var(--color-error,#ff5a4f)]/20 hover:text-[var(--color-error,#ff5a4f)]"
              >
                ×
              </button>
            )}
          </span>
        );
      })}
      {!readOnly && (
        adding ? (
          <span className="inline-flex items-center gap-1">
            <input
              autoFocus
              aria-label="New language code"
              value={draftCode}
              onChange={(e) => setDraftCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitAdd();
                if (e.key === 'Escape') {
                  setAdding(false);
                  setDraftCode('');
                }
              }}
              placeholder="fr"
              maxLength={8}
              className="w-12 rounded-md border border-[var(--color-border-default,#444)] bg-transparent px-1 py-0.5 text-xs uppercase outline-none"
            />
            <button
              type="button"
              onClick={commitAdd}
              className="rounded-md px-2 py-0.5 text-[10px] font-semibold text-[var(--color-accent,#b262f5)]"
            >
              Add
            </button>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="rounded-md px-2 py-1 text-[10px] font-semibold text-[var(--color-fg-muted,#888)] hover:text-[var(--color-accent,#b262f5)]"
          >
            + Add language
          </button>
        )
      )}
    </div>
  );
}

// ── Drag-and-drop helpers ─────────────────────────────────────────────
function useDragHandlers<T extends number>(onDrop: (from: T, to: T) => void) {
  const [draggingFrom, setDraggingFrom] = useState<T | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<T | null>(null);

  function onDragStart(e: ReactDragEvent<HTMLElement>, from: T) {
    setDraggingFrom(from);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(from));
    // Don't bubble — nested draggables (question inside step) otherwise
    // both fire and the step would move when the user meant to reorder
    // a question.
    e.stopPropagation();
  }

  function onDragOver(e: ReactDragEvent<HTMLElement>, over: T) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(over);
  }

  function onDropFn(e: ReactDragEvent<HTMLElement>, to: T) {
    e.preventDefault();
    e.stopPropagation();
    if (draggingFrom === null) return;
    if (draggingFrom !== to) onDrop(draggingFrom, to);
    setDraggingFrom(null);
    setDragOverIndex(null);
  }

  function onDragEnd() {
    setDraggingFrom(null);
    setDragOverIndex(null);
  }

  return {
    draggingFrom,
    dragOverIndex,
    onDragStart,
    onDragOver,
    onDrop: onDropFn,
    onDragEnd,
  };
}

// ── Utilities ─────────────────────────────────────────────────────────
function genId(prefix: string): string {
  // Stable enough for client-side keys; the server assigns real ids on save.
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

